import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API_BASE } from "../lib/apiConfig";
import { captureError, captureMessage, setSentryUser } from "../lib/sentry";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastFocusFetchRef = useRef(0);

  const prepareUser = useCallback((userData) => {
    if (!userData) return null;
    // Avatar objects can keep the same URL when another client replaces them.
    // Bust the browser cache whenever the authoritative profile is refreshed.
    return {
      ...userData,
      avatar_cache_bust: userData.avatar_url ? Date.now() : undefined,
    };
  }, []);

  const storeCsrfToken = useCallback((csrfToken) => {
    // CSRF is not a login secret; it is kept in sessionStorage so API writes can
    // echo it in X-CSRF-Token while the JWT stays HttpOnly.
    if (csrfToken) {
      sessionStorage.setItem("csrf_token", csrfToken);
    } else {
      captureMessage("Authenticated response did not include a CSRF token", "warning");
    }
  }, []);

  const fetchCsrfToken = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/csrf`, { withCredentials: true });
      storeCsrfToken(response.data?.csrf_token);
      return response.data?.csrf_token || null;
    } catch (error) {
      captureError(error, { action: "fetch_csrf_token" });
      return null;
    }
  }, [storeCsrfToken]);

  const clearSession = useCallback(() => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("csrf_token");
    setUser(null);
    setSentryUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
      const refreshedUser = prepareUser(response.data);
      setUser(refreshedUser);
      setSentryUser(refreshedUser);
      await fetchCsrfToken();
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        clearSession();
      } else {
        // Mobile Safari/Chrome can surface transient network/CORS interruptions
        // during refresh. Keep the current user and let the next focus refetch recover.
        captureError(error, { action: "fetch_user_transient" });
      }
    } finally {
      setLoading(false);
    }
  }, [clearSession, fetchCsrfToken, prepareUser]);

  useEffect(() => {
    // Tokens used to live in localStorage. Remove any old value and rely on the
    // backend's HttpOnly auth cookie instead.
    localStorage.removeItem("token");
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const refetchOnFocus = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current < 5000) return;
      lastFocusFetchRef.current = now;
      fetchUser();
    };
    document.addEventListener("visibilitychange", refetchOnFocus);
    window.addEventListener("focus", refetchOnFocus);
    return () => {
      document.removeEventListener("visibilitychange", refetchOnFocus);
      window.removeEventListener("focus", refetchOnFocus);
    };
  }, [fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(
      `${API_BASE}/auth/login`,
      { email, password, remember_me: rememberMe },
      { withCredentials: true },
    );
    const { csrf_token, user: userData } = response.data;
    localStorage.removeItem("token");
    storeCsrfToken(csrf_token);
    if (!csrf_token) {
      captureMessage("Login completed without CSRF token", "warning", { method: "password" });
    }
    const authenticatedUser = prepareUser(userData);
    setUser(authenticatedUser);
    setSentryUser(authenticatedUser);
    return authenticatedUser;
  };

  const magicLogin = async (email, code, rememberMe = false) => {
    const response = await axios.post(
      `${API_BASE}/auth/magic-login`,
      { email, code, remember_me: rememberMe },
      { withCredentials: true },
    );
    const { csrf_token, user: userData } = response.data;
    localStorage.removeItem("token");
    storeCsrfToken(csrf_token);
    if (!csrf_token) {
      captureMessage("Login completed without CSRF token", "warning", { method: "magic" });
    }
    const authenticatedUser = prepareUser(userData);
    setUser(authenticatedUser);
    setSentryUser(authenticatedUser);
    return authenticatedUser;
  };

  const signup = async (email, password, name, country = "", defaultCurrency = "usd", homeCity = null, referralCode = "") => {
    const payload = { email, password, name, country, default_currency: defaultCurrency };
    if (referralCode.trim()) payload.referral_code = referralCode.trim().toUpperCase();
    if (homeCity) {
      payload.home_city = homeCity.name;
      payload.home_city_lat = homeCity.lat;
      payload.home_city_lng = homeCity.lng;
    }
    const response = await axios.post(`${API_BASE}/auth/signup`, payload, { withCredentials: true });
    return response.data;
  };

  const logout = async () => {
    let serverLogoutSucceeded = false;
    try {
      const csrfToken = sessionStorage.getItem("csrf_token");
      await axios.post(`${API_BASE}/auth/logout`, {}, {
        withCredentials: true,
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      });
      serverLogoutSucceeded = true;
    } catch (error) {
      if (error.response?.status !== 401) {
        captureError(error, { action: "logout" });
      }
    }
    clearSession();
    return serverLogoutSucceeded;
  };

  const googleLogin = async (sessionId, referralCode = "") => {
    const response = await axios.post(
      `${API_BASE}/auth/google-callback`,
      { session_id: sessionId, referral_code: referralCode.trim().toUpperCase() },
      { withCredentials: true },
    );
    const { csrf_token, user: userData } = response.data;
    localStorage.removeItem("token");
    storeCsrfToken(csrf_token);
    if (!csrf_token) {
      captureMessage("Login completed without CSRF token", "warning", { method: "google" });
    }
    const authenticatedUser = prepareUser(userData);
    setUser(authenticatedUser);
    setSentryUser(authenticatedUser);
    return authenticatedUser;
  };

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const updateCredits = (newCredits) => {
    if (user) {
      setUser({ ...user, credits: newCredits });
    }
  };

  const updateUser = (fields) => {
    if (user) {
      setUser({ ...user, ...fields });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      magicLogin,
      googleLogin,
      signup,
      logout,
      refreshUser,
      updateCredits,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
